import { FC, useEffect, useRef, useState } from "react"

import { ReactComponent as IconAdd } from "@common_assets/svg/plus.svg"
import { yupResolver } from "@hookform/resolvers/yup"
import { Box, Button, Stack, Typography } from "@mui/material"
import { useMutationPostClassesStudents } from "api/reactQuery/mutations/classesStudents"
import { useQueryClassesCandidates } from "api/reactQuery/queries/classesCandidates"
import Dialog from "components/common/dialog/dialog"
import { useIsBreakpointUp } from "hooks/breakpoint"
import { useDialog } from "hooks/dialog"
import { useClassId } from "hooks/navigation"
import { useCustomSnackbar } from "hooks/snackbar"
import { useAppDispatch, useAppSelector } from "hooks/store"
import mixpanel from "mixpanel-browser"
import { FormProvider, get, useFieldArray, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useQueryClient } from "react-query"
import { selectUser } from "store/auth/auth.selectors"
import {
  selectExistingStudent,
  selectNewStudents,
} from "store/utility/utility.selectors"
import {
  setClassList,
  setExisting,
  setNewStudents,
  setPage,
} from "store/utility/utility.slice"
import { SCROLLBAR_WIDTH, scrollbarStyles } from "themes/scrollbar"
import { IStudentCandidate } from "ts/interfaces/Student"
import { getErrorMessage } from "utils/api"
import { isSchoolPlan } from "utils/roleCheck"

import DialogClose from "./dialogClose"
import {
  defaultValues,
  emptyStudentValue,
  validationSchema,
} from "./DialogFormStudentsInfo.config"
import { IFormState } from "./DialogFormStudentsInfo.types"
import ExistingStudentFields from "./existingStudentFields"
import NewStudentFields from "./newStudentFields"
import StudentsSearch from "./studentsSearch"

// The "isOpenInfo" marker keeps the dialog open across the TableView/ListingView
// remount a breakpoint change triggers. It must not survive a full page reload,
// though — the draft it referred to lived in Redux and is gone after a refresh.
// This runs once per page load (not on a component remount), clearing a stale
// marker so a refresh always lands on a closed dialog.
localStorage.removeItem("isOpenInfo")

interface IProps {
  isOpen: boolean
  handleClose: () => void
  refetch: () => void
}

const DialogFormStudentsInfo: FC<IProps> = ({
  isOpen,
  handleClose,
  refetch,
}) => {
  const queryClient = useQueryClient()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const { showSnackbar } = useCustomSnackbar()
  const classId = useClassId()
  const newStudents = useAppSelector(selectNewStudents)
  const exist = useAppSelector(selectExistingStudent)
  const isTablet = useIsBreakpointUp("tablet")
  const userInfo = useAppSelector(selectUser)
  const [isChangeEventType, setIsChangeEventyType] = useState(false)
  const hasRestoredDraftRef = useRef(false)

  useEffect(() => {
    isOpen && localStorage.setItem("isOpenInfo", JSON.stringify(true))
  }, [isOpen])

  const [existingStudents, setExistingStudents] =
    useState<IStudentCandidate[]>(exist)

  const { data, isLoading, isError } = useQueryClassesCandidates({
    classId: Number(classId),
  })

  useEffect(() => {
    if (isChangeEventType) {
      mixpanel.track("add_students", {
        action: "search_student",
      })
    }
  }, [isChangeEventType])

  const { isLoading: isLoadingPost, mutate: postStudents } =
    useMutationPostClassesStudents({
      options: {
        onSuccess: (_, variables) => {
          if (!!window.Intercom) {
            window.Intercom("trackEvent", "number of students added", {
              classId: classId,
              students:
                variables.data.existing_student_ids.length +
                variables.data.new_students.length,
            })
          }
          window.dataLayer = window.dataLayer || []
          window.dataLayer.push({
            event: "add_students",
            allStudents:
              variables.data.existing_student_ids.length +
              variables.data.new_students.length,
          })

          showSnackbar({
            title: t(
              variables.data.existing_student_ids.length +
                variables.data.new_students.length <=
                1
                ? "students.studentAddedSuccessfully"
                : "students.studentsAddedSuccessfully",
            ),
          })
          queryClient.invalidateQueries(["classesStudents", Number(classId)])
          queryClient.invalidateQueries(["classesCandidates", Number(classId)])
          handleClose()
          localStorage.setItem("isOpenInfo", JSON.stringify(false))
          dispatch(setPage(0))
          dispatch(setClassList([]))
          dispatch(setNewStudents([]))
          dispatch(setExisting([]))
          setExistingStudents([])
          methods.reset()
          refetch()
        },
        onError: (error) => {
          const errorDetail = get(error, "response.data.detail", null)
          if (Array.isArray(errorDetail)) {
            errorDetail.forEach((err) => {
              if (typeof err === "object") {
                const errorIndex = get(err, "loc[2]", null)
                if (errorIndex !== null) {
                  methods.setError(`students.${errorIndex}.userName`, {
                    message: err.msg,
                  })
                }
              }
            })
          } else {
            showSnackbar({
              title: getErrorMessage(error),
              variant: "error",
            })
          }
        },
      },
    })

  const filteredExistingData = data
    ? [...data.data].filter(
        (el) => !existingStudents.some((student) => student.id === el.id),
      )
    : []

  const {
    isOpen: isOpenCloseConfirm,
    handleOpen: handleOpenCloseConfirm,
    handleClose: handleCloseCloseConfirm,
  } = useDialog()

  const methods = useForm<IFormState>({
    resolver: yupResolver(validationSchema),
    defaultValues,
  })

  const {
    fields: studentFields,
    append: appendStudent,
    remove: removeStudent,
    replace: replaceStudents,
  } = useFieldArray({
    control: methods.control,
    name: "students",
  })

  const onSubmit = (data: IFormState) => {
    if (!existingStudents.length && !data.students.length) {
      showSnackbar({
        title: t("students.youNeedToAddAtLeastOneStudent"),
        variant: "error",
      })
      return
    }
    postStudents({
      classId: Number(classId),
      data: {
        existing_student_ids: existingStudents.map((student) => student.id),
        new_students: data.students.map((student) => ({
          first_name: student.firstName,
          last_name: student.lastName,
          login: student.userName,
          password: student.password,
        })),
      },
    })
  }

  // Authoritatively mirror the persisted draft into the field array — including
  // an empty draft, so a "delete all rows" done in the other view is honoured
  // once the fresh (empty) value propagates after the table/listing swap.
  // newStudents only changes on submit, dialog close and this swap — never mid
  // edit — so an unconditional replace can't wipe a form the user is editing.
  useEffect(() => {
    replaceStudents(newStudents)
    hasRestoredDraftRef.current = true
  }, [newStudents])

  // Runs after the restore effect so a fresh open (empty draft) still lands on
  // one blank row instead of being cleared by replaceStudents([]).
  useEffect(() => {
    if (isOpen && studentFields.length === 0 && existingStudents.length === 0) {
      appendStudent(emptyStudentValue)
    }
  }, [isOpen])

  useEffect(() => {
    dispatch(setExisting(existingStudents))
  }, [existingStudents])

  useEffect(() => {
    return () => {
      // Persist only once this instance has had a chance to restore the draft.
      // Before that, an empty form is just "not initialised yet" (the table/
      // listing swap and the StrictMode remount both unmount mid-restore), and
      // persisting it would clobber the saved draft. Afterwards an empty form is
      // a real user action (all rows deleted) and must be persisted as empty.
      if (hasRestoredDraftRef.current) {
        dispatch(setNewStudents(methods.getValues("students")))
      }
    }
  }, [isTablet])

  return (
    <>
      <Dialog
        titleText={t("students.addStudent")}
        open={
          isOpen || JSON.parse(localStorage.getItem("isOpenInfo") ?? "false")
        }
        slotProps={{ paper: { sx: { height: "760px" } } }}
        contentWrapperProps={{
          sx: { display: "flex", flexDirection: "column", flex: 1 },
        }}
        onActionButtonClick={() => {
          methods.clearErrors()
          const students = methods.getValues("students")
          const userNames = new Set(students.map((student) => student.userName))
          if (userNames.size !== students.length) {
            showSnackbar({
              variant: "error",
              title: t("students.userNamesHaveToBeUnique"),
            })
            return
          }
          methods.handleSubmit(onSubmit)()
        }}
        onClose={handleOpenCloseConfirm}
        actionAcceptText={t("students.addToClass")}
        actionAcceptButtonProps={{
          disabled: isLoading,
        }}
        tabletMaxWidth="867px"
        desktopMaxWidth="867px"
        isLoading={isLoadingPost}
      >
        <Box
          width="100%"
          component="form"
          borderTop={(theme) => `1px solid ${theme.palette.mockup.primary95}`}
          pt="24px"
          display="flex"
          flexDirection="column"
          flex={1}
        >
          {userInfo && isSchoolPlan(userInfo) && (
            <StudentsSearch
              setIsChange={setIsChangeEventyType}
              onChange={(value) => {
                if (!value) return
                setExistingStudents([...existingStudents, value])
                mixpanel.track("add_students", {
                  action: "select_student",
                })
              }}
              onAddNewStudentClick={() => {
                appendStudent(emptyStudentValue)
              }}
              options={filteredExistingData}
              isLoading={isLoading}
              isError={isError}
            />
          )}
          <FormProvider {...methods}>
            <Stack
              gap={{ mobile: "16px", tablet: "32px" }}
              flex={1}
              justifyContent="space-between"
            >
              <Box sx={{ position: "relative" }}>
                <Box
                  sx={{
                    maxHeight: "340px",
                    overflowY: "auto",
                    pr: "6px",
                    pb: "32px",
                    ...scrollbarStyles,
                  }}
                >
                  {existingStudents.length === 0 &&
                  studentFields.length === 0 ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ py: 2, textAlign: "center" }}
                    >
                      {t("students.noStudentsAddedYet")}
                    </Typography>
                  ) : (
                    <Stack gap={{ mobile: "16px", tablet: "32px" }}>
                      {existingStudents.map((student) => (
                        <ExistingStudentFields
                          key={student.id}
                          student={student}
                          onDeleteClick={() => {
                            setExistingStudents([
                              ...existingStudents.filter(
                                (existingStudent) =>
                                  existingStudent.id !== student.id,
                              ),
                            ])
                            mixpanel.track("add_students", {
                              action: "trash_icon",
                            })
                          }}
                        />
                      ))}
                      {studentFields.map((field, index) => (
                        <NewStudentFields
                          key={field.id}
                          index={index}
                          onDeleteClick={() => {
                            removeStudent(index)
                            mixpanel.track("add_students", {
                              action: "trash_icon",
                            })
                          }}
                        />
                      ))}
                    </Stack>
                  )}
                </Box>
                <Box
                  aria-hidden="true"
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: `${SCROLLBAR_WIDTH}px`,
                    height: "48px",
                    background:
                      "linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.96) 100%)",
                    pointerEvents: "none",
                    zIndex: 1,
                  }}
                />
              </Box>
              <Button
                startIcon={<IconAdd />}
                variant="text"
                sx={{ width: "max-content" }}
                onClick={() => {
                  appendStudent(emptyStudentValue)
                  mixpanel.track("add_students", {
                    event_name: "add_another_student",
                    action: "open_form",
                  })
                }}
              >
                {t("students.addAnotherStudent")}
              </Button>
            </Stack>
          </FormProvider>
        </Box>
      </Dialog>
      <DialogClose
        open={isOpenCloseConfirm}
        onClose={handleCloseCloseConfirm}
        onAccept={() => {
          methods.reset()
          localStorage.setItem("isOpenInfo", JSON.stringify(false))
          dispatch(setNewStudents([]))
          dispatch(setExisting([]))
          setExistingStudents([])
          handleCloseCloseConfirm()
          handleClose()
          mixpanel.track("add_students", {
            event_name: "student_info",
            action: "cancel",
          })
        }}
      />
    </>
  )
}

export default DialogFormStudentsInfo
